<div class="we-wrap">
  <h2>Weight Estimator</h2>
  <p class="we-note">
    Vendor weights are always the source of truth — this tool only fills gaps.
  </p>

  <!-- stage rail: Backtest -> Generate -> Review -> Release -->
  <div class="we-stepper" [class.we-stepper-idle]="currentStage === 0">
    <ng-container *ngFor="let s of stages; let last = last; trackBy: trackStage">
      <div class="we-step" [ngClass]="'we-step-' + s.state" (click)="scrollTo(s.anchor)">
        <span class="we-step-num">
          <ng-container *ngIf="s.state === 'done'; else stepNum">&#10003;</ng-container>
          <ng-template #stepNum>{{s.n}}</ng-template>
        </span>
        <span class="we-step-body">
          <span class="we-step-label">{{s.label}}</span>
          <span class="we-step-status">{{s.status}}</span>
        </span>
      </div>
      <span class="we-step-arrow" *ngIf="!last">&#8594;</span>
    </ng-container>
  </div>
  <div class="we-next-hint">
    <span class="we-tag we-tag-accent">Next</span> {{nextStepHint}}
  </div>

  <!-- stages 1-2: vendor + params -->
  <div class="we-row" id="we-setup">
    <mat-card class="we-card we-vendor-card">
      <h3>Vendor</h3>
      <mat-form-field appearance="fill" class="we-full">
        <mat-label>Filter vendors</mat-label>
        <input matInput [(ngModel)]="vendorFilter" (input)="applyVendorFilter()">
      </mat-form-field>
      <mat-form-field appearance="fill" class="we-full">
        <mat-label>Vendor</mat-label>
        <mat-select [(ngModel)]="selectedVendor" (selectionChange)="onVendorChange()" panelClass="we-vendor-panel">
          <mat-select-trigger>
            {{selectedVendor}}
            <span class="we-vendor-trigger-sub" *ngIf="selectedVendorInfo">
              {{selectedVendorInfo.missing | number}} missing &middot; {{selectedVendorInfo.control | number}} control
            </span>
          </mat-select-trigger>
          <mat-option *ngFor="let v of filteredVendors" [value]="v.vendor">
            <span class="we-vendor-opt-name">{{v.vendor}}</span>
            <span class="we-vendor-opt-sub">{{v.missing | number}} missing &middot; {{v.control | number}} control</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <div *ngIf="selectedVendorInfo" class="we-vendor-stats">
        <div><b>{{selectedVendorInfo.missing | number}}</b> products need a weight</div>
        <div><b>{{selectedVendorInfo.control | number}}</b> control products with real weights</div>
      </div>
    </mat-card>

    <mat-card class="we-card we-params-card">
      <div class="we-card-head">
        <h3>Tune &amp; run</h3>
        <span class="we-tag we-tag-warn" *ngIf="paramsDirty" matTooltip="Parameters differ from the defaults">custom params</span>
        <span class="we-tag" *ngIf="presetLoaded && !paramsDirty" matTooltip="This vendor's saved preset is loaded">vendor preset</span>
        <span class="we-flex-spacer"></span>
        <span class="we-card-summary" *ngIf="tuneCardCollapsed">{{tuneSummary}}</span>
        <button type="button" class="we-collapse-btn" (click)="toggleTuneCard()"
                [attr.aria-expanded]="!tuneCardCollapsed"
                [matTooltip]="tuneCardCollapsed ? 'Expand' : 'Collapse'">{{tuneCardCollapsed ? '&#9656;' : '&#9662;'}}</button>
      </div>
      <ng-container *ngIf="!tuneCardCollapsed">
      <div class="we-params-grid we-params-primary">
        <mat-form-field appearance="fill">
          <mat-label>Quantile (over-bias)</mat-label>
          <input matInput type="number" step="0.05" min="0.5" max="0.95" [(ngModel)]="params.quantile">
          <mat-hint>Higher = errs heavier (0.5&ndash;0.95)</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Safety margin (lbs)</mat-label>
          <input matInput type="number" step="0.25" min="0" max="20" [(ngModel)]="params.safetyAddLbs">
          <mat-hint>Flat lbs added to every estimate</mat-hint>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Backtest sample (0 = all)</mat-label>
          <input matInput type="number" step="500" min="0" [(ngModel)]="params.sampleSize">
          <mat-hint>Control products to test on</mat-hint>
        </mat-form-field>
      </div>

      <button type="button" class="we-advanced-toggle" (click)="showAdvanced = !showAdvanced">
        <span class="we-advanced-caret">{{showAdvanced ? '&#9662;' : '&#9656;'}}</span>
        <span class="we-advanced-label">Advanced settings</span>
        <span class="we-advanced-sub">matching rules, neighbor counts, caps &amp; estimation tiers</span>
      </button>

      <div *ngIf="showAdvanced" class="we-advanced">
        <div class="we-adv-section">
          <div class="we-adv-head">Matching rules</div>
          <div class="we-params-grid">
            <mat-form-field appearance="fill">
              <mat-label>Dup agreement %</mat-label>
              <input matInput type="number" step="5" min="0" [(ngModel)]="params.dupAgreementPct"
                     matTooltip="How closely weights on duplicate-SKU copies must agree before we trust them">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Prefix min length</mat-label>
              <input matInput type="number" min="2" [(ngModel)]="params.prefixMinLen"
                     matTooltip="Shortest SKU prefix that still counts as a family">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Prefix min neighbors</mat-label>
              <input matInput type="number" min="1" [(ngModel)]="params.prefixMinNeighbors"
                     matTooltip="A SKU family needs at least this many weighted members to be used">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Size-match neighbors K</mat-label>
              <input matInput type="number" min="1" [(ngModel)]="params.sizeNearestK"
                     matTooltip="Within a SKU family, estimate from the K neighbors with the closest numeric segments (sizes)">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Text top-K</mat-label>
              <input matInput type="number" min="5" [(ngModel)]="params.textTopK"
                     matTooltip="Most-similar descriptions considered per product">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Text min neighbors</mat-label>
              <input matInput type="number" min="1" [(ngModel)]="params.textMinNeighbors"
                     matTooltip="Minimum similar descriptions required for a text-based estimate">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Type min samples</mat-label>
              <input matInput type="number" min="2" [(ngModel)]="params.typeMinSamples"
                     matTooltip="Minimum weighted products a Brand+Type group needs before it can estimate">
            </mat-form-field>
          </div>
        </div>

        <div class="we-adv-section">
          <div class="we-adv-head">Estimation tiers</div>
          <div class="we-tier-toggles">
            <mat-checkbox [(ngModel)]="params.tiers.weightTag" matTooltip="Recover vendor weight from Weight_x LB product tags">Weight tag</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.tiers.duplicate">Duplicate SKU</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.tiers.prefix">SKU family</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.sizeFit" matTooltip="When a numeric SKU segment tracks weight across a family, predict from that trend — can extrapolate above every sibling">Size-trend fit</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.tiers.descSize" matTooltip="Sizes parsed from the description (4 in, 60mm, 150 psi) regressed against weight — fires when SKU-family evidence is absent">Description size</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.tiers.text">Description</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.tiers.type">Brand+Type</mat-checkbox>
          </div>
        </div>

        <div class="we-adv-section">
          <div class="we-adv-head">Floors, caps &amp; bands</div>
          <div class="we-tier-toggles">
            <mat-checkbox [(ngModel)]="params.priceFloor" matTooltip="Floor estimates at the per-type price/weight fit — expensive parts are rarely light">Price floor</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.typeSanityBand" matTooltip="Clamp estimates into the weight range the item's own type actually occupies">Type sanity band</mat-checkbox>
            <mat-checkbox [(ngModel)]="params.treatOneAsMissing" matTooltip="weight_lbs = 1 is a suspected placeholder">Treat weight 1 as missing</mat-checkbox>
          </div>
          <div class="we-params-grid">
            <mat-form-field appearance="fill">
              <mat-label>Max weight cap (lbs)</mat-label>
              <input matInput type="number" min="1" [(ngModel)]="params.maxWeightCap"
                     matTooltip="Hard ceiling — no estimate can exceed this">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Dup family-floor %</mat-label>
              <input matInput type="number" step="5" min="0" [(ngModel)]="params.dupFamilyFloorPct"
                     matTooltip="A lone duplicate twin is overridden by the SKU-family estimate when the family exceeds it by this %">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Size-fit min correlation</mat-label>
              <input matInput type="number" step="0.05" min="0" max="1" [(ngModel)]="params.sizeFitMinCorrelation"
                     matTooltip="How strongly a SKU segment must track weight before the size-trend fit is trusted">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Bimodal ratio (0 = off)</mat-label>
              <input matInput type="number" step="0.5" min="0" [(ngModel)]="params.bimodalRatio"
                     matTooltip="Neighbor pools whose heaviest/lightest exceed this ratio pick one cluster instead of averaging across the split">
            </mat-form-field>
            <mat-form-field appearance="fill">
              <mat-label>Freight threshold (lbs)</mat-label>
              <input matInput type="number" step="5" min="0" [(ngModel)]="params.freightThresholdLbs"
                     matTooltip="Estimates near this weight get review priority — misses here change the shipping class">
            </mat-form-field>
          </div>
        </div>
      </div>

      <div class="we-actions">
        <button *ngIf="primaryAction === 'backtest'" mat-raised-button color="primary"
                [disabled]="!selectedVendor || runInProgress" (click)="startBacktest()"
                matTooltip="Stage 1 — score the algorithm against control products; touches nothing">
          Run Backtest
        </button>
        <button *ngIf="primaryAction !== 'backtest'" mat-stroked-button
                [disabled]="!selectedVendor || runInProgress" (click)="startBacktest()"
                matTooltip="Stage 1 — score the algorithm against control products; touches nothing">
          Run Backtest
        </button>
        <button *ngIf="primaryAction === 'generate'" mat-raised-button color="primary"
                [disabled]="!selectedVendor || runInProgress" (click)="startGenerate()"
                matTooltip="Stage 2 — propose weights for the gaps; proposals go to the review queue">
          Generate Estimates
        </button>
        <button *ngIf="primaryAction !== 'generate'" mat-stroked-button
                [disabled]="!selectedVendor || runInProgress" (click)="startGenerate()"
                matTooltip="Stage 2 — propose weights for the gaps; proposals go to the review queue">
          Generate Estimates
        </button>
        <span class="we-flex-spacer"></span>
        <button mat-button [disabled]="!paramsDirty" (click)="resetParams()">Reset params</button>
        <button mat-stroked-button [disabled]="!selectedVendor" (click)="savePreset()"
                matTooltip="Store these parameters as this vendor's preset — auto-loads whenever the vendor is selected">
          Save as vendor preset
        </button>
      </div>
      </ng-container>
      <div *ngIf="runInProgress" class="we-progress">
        <mat-progress-bar [mode]="progress.total ? 'determinate' : 'indeterminate'" [value]="progressPct"></mat-progress-bar>
        <span>{{progress.phase}} {{progress.done | number}} / {{progress.total | number}}</span>
      </div>
    </mat-card>
  </div>

  <!-- run history + scorecard -->
  <div class="we-row" id="we-scorecard" *ngIf="selectedVendor">
    <mat-card class="we-card we-runs-card">
      <h3>Run history</h3>
      <p class="we-card-hint">Opening a run shows its scorecard and restores the parameters it used.</p>
      <table class="we-table">
        <thead>
          <tr>
            <th>Started</th><th>Kind</th><th>Status</th><th class="num">Sample</th><th class="num">Under %</th><th class="num">Coverage %</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let run of runs" class="we-clickable" (click)="openRun(run)"
              [class.we-active-row]="activeRun && activeRun._id === run._id"
              matTooltip="Open this run's scorecard and restore the parameters it used">
            <td>{{run.started_at | date:'MM/dd HH:mm'}}</td>
            <td>{{run.kind}}</td>
            <td [class.we-failed]="run.status === 'failed'">{{run.status}}</td>
            <td class="num">{{run.sample_size || run.gap_total || '-'}}</td>
            <td class="num">{{run.metrics?.underRate ?? '-'}}</td>
            <td class="num">{{run.metrics?.coverage ?? '-'}}</td>
          </tr>
        </tbody>
      </table>
      <p *ngIf="!runs.length" class="we-empty">No runs yet for this vendor — start with a backtest.</p>
    </mat-card>

    <mat-card class="we-card we-metrics-card" *ngIf="activeRun?.metrics as m; else scorecardEmpty">
      <h3>Scorecard <span class="we-scorecard-sub">{{activeRun?.kind}} &middot; {{activeRun?.started_at | date:'MM/dd HH:mm'}}</span></h3>

      <ng-container *ngIf="activeRun?.kind === 'backtest'">
        <div *ngIf="activeRun?.status === 'complete'" class="we-verdict"
             [class.we-verdict-ok]="isPassing(m)" [class.we-verdict-bad]="!isPassing(m)">
          <div class="we-verdict-text" *ngIf="isPassing(m); else verdictBad">
            <b>Backtest passed.</b> No control item was under-estimated by more than 1&nbsp;lb — the algorithm
            errs heavy, as intended. Safe to generate estimates.
          </div>
          <ng-template #verdictBad>
            <div class="we-verdict-text">
              <b>Needs tuning.</b> {{m.undersOver1Lb | number}} control item(s) would ship underpriced by more than
              1&nbsp;lb. Raise <b>Quantile</b> or <b>Safety margin</b> (higher = errs heavier) and rerun.
            </div>
          </ng-template>
          <button *ngIf="isPassing(m)" mat-raised-button color="accent" [disabled]="runInProgress" (click)="startGenerate()">
            Generate Estimates
          </button>
          <button *ngIf="!isPassing(m)" mat-stroked-button (click)="inspectUnders()">Inspect the unders</button>
        </div>

        <div class="we-scorecard">
          <div class="we-gate-group">
            <span class="we-gate-label">Ship-safe gate</span>
            <div class="we-gate-metrics">
              <div class="we-metric we-metric-gate" [class.we-metric-bad]="m.undersOver1Lb > 0"
                   [class.we-clickable]="m.undersOver1Lb > 0" (click)="onUndersMetric(m)">
                <span class="we-metric-value">{{m.undersOver1Lb ?? '-'}}</span>
                <span class="we-metric-label">Unders worse than 1 lb</span>
                <span class="we-metric-caption" *ngIf="m.undersOver1Lb > 0">Would ship underpriced — click to inspect</span>
                <span class="we-metric-caption" *ngIf="m.undersOver1Lb === 0">None — this is the goal</span>
              </div>
              <div class="we-metric we-metric-gate" [class.we-metric-bad]="m.undersOver5Lb > 0">
                <span class="we-metric-value">{{m.undersOver5Lb ?? '-'}}</span>
                <span class="we-metric-label">Unders worse than 5 lbs</span>
                <span class="we-metric-caption" *ngIf="m.undersOver5Lb > 0">Serious misses — must be zero</span>
              </div>
              <div class="we-metric we-metric-gate" [class.we-metric-bad]="m.maxUnderLbs > 5">
                <span class="we-metric-value">{{m.maxUnderLbs ?? '-'}} lbs</span>
                <span class="we-metric-label">Worst under</span>
                <span class="we-metric-caption">Single worst under-estimate</span>
              </div>
              <div class="we-metric we-metric-gate" [class.we-metric-bad]="m.underRate > 5">
                <span class="we-metric-value">{{m.underRate}}%</span>
                <span class="we-metric-label">Under rate</span>
                <span class="we-metric-caption">Estimated below actual — target &le; 5%</span>
              </div>
            </div>
          </div>
          <div class="we-info-metrics">
            <div class="we-metric">
              <span class="we-metric-value">{{m.medianAbsErrLbs ?? '-'}} lbs</span>
              <span class="we-metric-label">Median |error|</span>
            </div>
            <div class="we-metric">
              <span class="we-metric-value">{{m.medianOverLbs ?? '-'}} lbs</span>
              <span class="we-metric-label">Median over</span>
              <span class="we-metric-caption">Typical padding added</span>
            </div>
            <div class="we-metric">
              <span class="we-metric-value">{{m.within1Lb ?? '-'}}%</span>
              <span class="we-metric-label">Within &plusmn;1 lb</span>
            </div>
            <div class="we-metric">
              <span class="we-metric-value">{{m.coverage}}%</span>
              <span class="we-metric-label">Coverage</span>
              <span class="we-metric-caption">Control items that got a prediction</span>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="activeRun?.kind === 'generate'">
        <div *ngIf="activeRun?.status === 'complete'" class="we-verdict we-verdict-ok">
          <div class="we-verdict-text">
            <b>{{m.proposed | number}} estimates proposed.</b> Nothing was written to products — they are waiting
            in the review queue below.
          </div>
          <button mat-raised-button color="primary" (click)="scrollTo('we-review')">Review them</button>
        </div>
        <div class="we-scorecard">
          <div class="we-metric">
            <span class="we-metric-value">{{m.gapTotal | number}}</span>
            <span class="we-metric-label">Products missing weight</span>
          </div>
          <div class="we-metric">
            <span class="we-metric-value">{{m.proposed | number}}</span>
            <span class="we-metric-label">Estimates proposed</span>
          </div>
          <div class="we-metric">
            <span class="we-metric-value">{{m.coverage}}%</span>
            <span class="we-metric-label">Coverage</span>
          </div>
        </div>
      </ng-container>

      <div *ngIf="activeRun?.kind === 'backtest' && m.byTier" class="we-breakdowns">
        <table class="we-table">
          <thead>
            <tr><th>Tier</th><th class="num">N</th><th class="num">Under %</th><th class="num">Median |err| lbs</th></tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let tier of tierKeys">
              <tr *ngIf="m.byTier[tier] as t">
                <td>{{tierLabel(tier)}}</td><td class="num">{{t.n | number}}</td><td class="num">{{t.underRate}}</td><td class="num">{{t.medianAbsErrLbs}}</td>
              </tr>
            </ng-container>
          </tbody>
        </table>
        <table class="we-table">
          <thead>
            <tr><th>Weight band</th><th class="num">N</th><th class="num">Under %</th><th class="num">Median |err| lbs</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let b of m.byBand">
              <td>{{b.band}}</td><td class="num">{{b.n | number}}</td><td class="num">{{b.underRate}}</td><td class="num">{{b.medianAbsErrLbs}}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div *ngIf="activeRun?.status === 'failed'" class="we-failed">Error: {{activeRun?.error}}</div>
    </mat-card>

    <ng-template #scorecardEmpty>
      <mat-card class="we-card we-metrics-card">
        <h3>Scorecard</h3>
        <div *ngIf="activeRun?.status === 'failed'" class="we-failed">Error: {{activeRun?.error}}</div>
        <p *ngIf="activeRun?.status !== 'failed'" class="we-empty">
          Run a backtest to score the algorithm here — you get a clear pass / needs-tuning verdict
          before anything touches products.
        </p>
      </mat-card>
    </ng-template>
  </div>

  <!-- stage 1 drill-down: backtest results -->
  <mat-card class="we-card" id="we-results" *ngIf="activeRun && activeRun.kind === 'backtest' && activeRun.status === 'complete'">
    <h3>Backtest results <small>(worst unders first)</small></h3>
    <p class="we-card-hint">Click any row to see exactly how that number was produced.</p>
    <div class="we-filters">
      <mat-checkbox [checked]="!!resultsFilter.under" (change)="toggleUnderFilter()">Unders only</mat-checkbox>
      <mat-form-field appearance="fill" class="we-dense-field">
        <mat-label>Tier</mat-label>
        <mat-select [value]="resultsFilter.tier || ''" (selectionChange)="setResultsTier($event.value)">
          <mat-option value="">All</mat-option>
          <mat-option value="weight_tag">Weight tag</mat-option>
          <mat-option value="duplicate">Duplicate SKU</mat-option>
          <mat-option value="prefix">SKU family</mat-option>
          <mat-option value="desc_size">Description size</mat-option>
          <mat-option value="text">Description</mat-option>
          <mat-option value="type">Brand+Type</mat-option>
          <mat-option value="none">No prediction</mat-option>
        </mat-select>
      </mat-form-field>
      <span class="we-count">{{resultsTotal | number}} rows</span>
    </div>
    <table class="we-table">
      <thead>
        <tr><th>SKU</th><th class="num">Actual (lbs)</th><th class="num">Predicted (lbs)</th><th class="num">&Delta; lbs</th><th>Tier</th><th class="num">Neighbors</th></tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let r of results">
          <tr [class.we-under-row]="r.under" class="we-clickable" (click)="toggleDetail(r)"
              matTooltip="Click to see how this number was produced">
            <td>{{r.sku}}</td>
            <td class="num">{{r.actual}}</td>
            <td class="num">{{r.predicted ?? '-'}}</td>
            <td class="num" [class.we-under]="r.delta_lbs < 0">{{r.delta_lbs > 0 ? '+' : ''}}{{r.delta_lbs ?? '-'}}</td>
            <td>{{tierLabel(r.tier)}}</td>
            <td class="num">{{r.neighbors}}</td>
          </tr>
          <tr *ngIf="detailRow === r">
            <td colspan="6" class="we-explain">
              <ng-container *ngTemplateOutlet="explainTpl; context: {$implicit: r.explain}"></ng-container>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
    <div class="we-pager" *ngIf="resultsPages > 1">
      <button mat-button [disabled]="resultsPage === 0" (click)="loadResults(resultsPage - 1)">Prev</button>
      <span>Page {{resultsPage + 1}} / {{resultsPages}}</span>
      <button mat-button [disabled]="resultsPage >= resultsPages - 1" (click)="loadResults(resultsPage + 1)">Next</button>
    </div>
  </mat-card>

  <!-- stages 3-4: review queue + release -->
  <mat-card class="we-card" id="we-review" *ngIf="selectedVendor">
    <h3>Review &amp; release</h3>
    <p class="we-card-hint">
      Approve estimates you trust, reject the rest. Release writes the approved weights to products —
      items that received a real vendor weight since approval are skipped automatically.
    </p>
    <div class="we-filter-bar">
      <div class="we-status-chips">
        <button type="button" class="we-status-chip" [class.we-chip-active]="!estimatesFilter.status"
                (click)="setEstimatesStatus('')">All</button>
        <button type="button" class="we-status-chip" [class.we-chip-active]="estimatesFilter.status === 'proposed'"
                (click)="setEstimatesStatus('proposed')">Proposed <b>{{(estimateCounts['proposed'] || 0) | number}}</b></button>
        <button type="button" class="we-status-chip" [class.we-chip-active]="estimatesFilter.status === 'approved'"
                (click)="setEstimatesStatus('approved')">Approved <b>{{(estimateCounts['approved'] || 0) | number}}</b></button>
        <button type="button" class="we-status-chip" [class.we-chip-active]="estimatesFilter.status === 'rejected'"
                (click)="setEstimatesStatus('rejected')">Rejected <b>{{(estimateCounts['rejected'] || 0) | number}}</b></button>
        <button type="button" class="we-status-chip" [class.we-chip-active]="estimatesFilter.status === 'released'"
                (click)="setEstimatesStatus('released')">Released <b>{{(estimateCounts['released'] || 0) | number}}</b></button>
        <button type="button" class="we-status-chip" [class.we-chip-active]="estimatesFilter.status === 'reverted'"
                *ngIf="estimateCounts['reverted']"
                (click)="setEstimatesStatus('reverted')">Reverted <b>{{(estimateCounts['reverted'] || 0) | number}}</b></button>
      </div>
      <mat-form-field appearance="fill" class="we-dense-field">
        <mat-label>Sort</mat-label>
        <mat-select [value]="estimatesFilter.sort || 'confidence'" (selectionChange)="setEstimatesSort($event.value)">
          <mat-option value="risk">Risk (review these first)</mat-option>
          <mat-option value="confidence">Confidence</mat-option>
          <mat-option value="weight">Est. weight</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill" class="we-dense-field">
        <mat-label>Min confidence</mat-label>
        <input matInput type="number" step="0.05" min="0" max="1" [ngModel]="estimatesFilter.minConfidence"
               (change)="setMinConfidence($any($event.target).value)"
               matTooltip="Filter the queue (and bulk actions) to estimates at or above this confidence — e.g. 0.9 to bulk-approve only the safest">
      </mat-form-field>
    </div>
    <div class="we-action-bar">
      <button mat-stroked-button color="primary" [disabled]="!selectedEstimateIds.size" (click)="reviewSelected('approve')">Approve selected ({{selectedEstimateIds.size}})</button>
      <button mat-stroked-button [disabled]="!selectedEstimateIds.size" (click)="reviewSelected('reject')">Reject selected</button>
      <button mat-stroked-button [matMenuTriggerFor]="bulkMenu">Bulk&hellip;</button>
      <mat-menu #bulkMenu="matMenu">
        <button mat-menu-item (click)="reviewAllFiltered('approve')">Approve ALL filtered</button>
        <button mat-menu-item (click)="reviewAllFiltered('reject')">Reject ALL filtered</button>
        <button mat-menu-item *ngIf="estimateCounts['released'] > 0" (click)="revert()">
          Revert {{estimateCounts['released'] || 0}} released
        </button>
      </mat-menu>
      <span class="we-flex-spacer"></span>
      <button mat-raised-button color="warn"
              [disabled]="!(estimateCounts['approved'] > 0)" (click)="release()"
              matTooltip="Stage 4 — writes approved weights to products through the normal save pipeline">
        Release {{estimateCounts['approved'] || 0}} approved
      </button>
    </div>
    <table class="we-table">
      <thead>
        <tr>
          <th><mat-checkbox [checked]="selectedEstimateIds.size === estimates.length && estimates.length > 0" (change)="toggleAllEstimates()"></mat-checkbox></th>
          <th>SKU</th><th class="num">Est. weight (lbs)</th><th>Tier</th><th class="num">Neighbors</th><th class="num">Confidence</th><th>Status</th><th>Reviewed by</th><th></th>
        </tr>
      </thead>
      <tbody>
        <ng-container *ngFor="let e of estimates">
          <tr class="we-clickable" (click)="toggleDetail(e)" matTooltip="Click to see how this estimate was produced">
            <td (click)="$event.stopPropagation()"><mat-checkbox [checked]="selectedEstimateIds.has(e._id)" (change)="toggleEstimate(e._id)"></mat-checkbox></td>
            <td>
              {{e.sku}}
              <span class="we-tag we-tag-critical" *ngIf="e.requeued_reason" [matTooltip]="e.requeued_reason">flagged by a miss</span>
            </td>
            <td class="num">
              <b>{{e.est_weight}}</b>
              <span class="we-tag we-tag-warn" *ngIf="nearFreightThreshold(e)"
                    matTooltip="Near the freight threshold — a miss here changes the shipping class">near freight line</span>
            </td>
            <td>{{tierLabel(e.tier)}}</td>
            <td class="num">{{e.neighbors}}</td>
            <td class="num">{{e.confidence | percent}}</td>
            <td>{{e.status}}</td>
            <td>{{e.reviewed_by?.name || '-'}}</td>
            <td (click)="$event.stopPropagation()">
              <button mat-button color="primary" *ngIf="e.status !== 'approved' && e.status !== 'released'" (click)="reviewOne(e, 'approve')">Approve</button>
              <button mat-button color="warn" *ngIf="e.status !== 'rejected' && e.status !== 'released'" (click)="reviewOne(e, 'reject')">Reject</button>
            </td>
          </tr>
          <tr *ngIf="detailRow === e">
            <td colspan="9" class="we-explain">
              <ng-container *ngTemplateOutlet="explainTpl; context: {$implicit: e.explain}"></ng-container>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>
    <p *ngIf="!estimates.length" class="we-empty">No estimates for this filter — run "Generate Estimates" first.</p>
    <div class="we-pager" *ngIf="estimatesPages > 1">
      <button mat-button [disabled]="estimatesPage === 0" (click)="loadEstimates(estimatesPage - 1)">Prev</button>
      <span>Page {{estimatesPage + 1}} / {{estimatesPages}}</span>
      <button mat-button [disabled]="estimatesPage >= estimatesPages - 1" (click)="loadEstimates(estimatesPage + 1)">Next</button>
    </div>
  </mat-card>

  <!-- real-world accuracy: corrections people made over released estimates -->
  <mat-card class="we-card" *ngIf="selectedVendor && misses.length">
    <div class="we-card-head we-clickable" (click)="missesOpen = !missesOpen">
      <h3>Real-world misses</h3>
      <span class="we-tag we-tag-critical">{{misses.length | number}}</span>
      <span class="we-flex-spacer"></span>
      <button type="button" class="we-collapse-btn" [attr.aria-expanded]="missesOpen"
              (click)="$event.stopPropagation(); missesOpen = !missesOpen">{{missesOpen ? '&#9662;' : '&#9656;'}}</button>
    </div>
    <ng-container *ngIf="missesOpen">
      <p class="we-card-hint">
        Weights someone corrected after a release — the live accuracy feed. Material misses automatically
        re-queue released estimates that were built from the same neighbors.
      </p>
      <table class="we-table">
        <thead>
          <tr><th>When</th><th>SKU</th><th class="num">We estimated</th><th class="num">Real weight</th><th class="num">&Delta; lbs</th><th>Tier</th><th class="num">Siblings re-queued</th><th>Corrected by</th></tr>
        </thead>
        <tbody>
          <tr *ngFor="let miss of misses">
            <td>{{miss.created_at | date:'MM/dd HH:mm'}}</td>
            <td>{{miss.sku}}</td>
            <td class="num">{{miss.est_weight}} lbs</td>
            <td class="num"><b>{{miss.real_weight}} lbs</b></td>
            <td class="num" [class.we-under]="miss.delta_lbs < 0">{{miss.delta_lbs > 0 ? '+' : ''}}{{miss.delta_lbs}}</td>
            <td>{{tierLabel(miss.tier)}}</td>
            <td class="num">{{miss.siblings_requeued || 0}}</td>
            <td>{{miss.user?.name || '-'}}</td>
          </tr>
        </tbody>
      </table>
    </ng-container>
  </mat-card>
</div>

<!-- how-was-this-number-produced panel, shared by backtest results and review queue -->
<ng-template #explainTpl let-ex>
  <div *ngIf="ex; else noExplain">
    <div class="we-explain-basis">{{ex.basis}}</div>
    <div *ngIf="ex.duplicateWeights?.length" class="we-explain-line">
      Duplicate copy weight(s): <b>{{ex.duplicateWeights.join(' lbs, ')}} lbs</b>
    </div>
    <div *ngIf="ex.neighbors?.length" class="we-explain-neighbors">
      <span class="we-explain-line">Neighbors used (lightest to heaviest):</span>
      <span class="we-chip" *ngFor="let n of ex.neighbors">{{n.sku}} · {{n.lbs}} lbs</span>
    </div>
    <div *ngIf="ex.quantileValue != null" class="we-explain-line">
      Percentile picked <b>{{ex.quantileValue}} lbs</b> from that list, then the safety multiplier was applied.
    </div>
    <div *ngIf="ex.priceFloorApplied" class="we-explain-line">
      Price floor engaged: raised to <b>{{ex.priceFloor}} lbs</b><span *ngIf="ex.raisedFrom != null"> (from {{ex.raisedFrom}} lbs)</span> because this part's price implies it can't be that light.
    </div>
    <div *ngIf="ex.familyFloorApplied" class="we-explain-line">
      Family floor engaged: the lone duplicate copy said <b>{{ex.raisedFrom}} lbs</b> but the SKU family estimates <b>{{ex.familyEstimate}} lbs</b> — the family wins (lone copies are systematically stale-low).
    </div>
    <div *ngIf="ex.bimodal" class="we-explain-line">
      This family splits into two weight groups ({{ex.bimodal.clusters[0]}} light / {{ex.bimodal.clusters[1]}} heavy) — {{ex.bimodal.picked}}.
    </div>
    <div *ngIf="ex.sanityRaisedTo != null" class="we-explain-line">
      Sanity band: raised to <b>{{ex.sanityRaisedTo}} lbs</b> — nothing in this product's category weighs less.
    </div>
    <div *ngIf="ex.sanityCappedTo != null" class="we-explain-line">
      Sanity band: capped at <b>{{ex.sanityCappedTo}} lbs</b> — far above anything in this product's category.
    </div>
  </div>
  <ng-template #noExplain><span class="we-empty">No breakdown recorded for this row (run predates the explain feature — rerun to get one).</span></ng-template>
</ng-template>
